import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getElementMetaObject } from '@src/utility/extraElementData';

import { ColorControl } from './controls/ColorControl';
import { FontControl } from './controls/FontControl';
import { ImageControl } from './controls/ImageControl';
import { NumberControl } from './controls/NumberControl';
import { SpeakerControl } from './controls/SpeakerControl';
import { TextControl } from './controls/TextControl';
import { VideoControl } from './controls/VideoControl';

import type { ControlSetting } from './ControlTypes';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';

interface Props {
  control: ControlSetting;
  activeElement: ElementState | null | undefined;
  modifyProperty: (
    elementPaths: string[][],
    property: string,
    value: unknown,
  ) => void;
  handleSpeakerControlChange: (
    controlBindings: Record<string, string>,
    inputValues: Speaker, // This could be other things in the future, but for now we're only dealing with speakers
  ) => void;
}
export const CompositionControl = ({
  control,
  activeElement,
  modifyProperty,
  handleSpeakerControlChange,
}: Props) => {
  if (!activeElement) return null;
  // Helper: traverse from the active element through a numeric path array to get the descendant's source object.
  const videoCreator = useVideoCreatorStore();
  interface HasSource {
    source?: unknown;
  }
  const hasSource = (val: unknown): val is HasSource =>
    !!val &&
    typeof val === 'object' &&
    'source' in (val as Record<string, unknown>);

  const getSourceForPath = (
    path: string[],
  ): Record<string, unknown> | undefined => {
    let node: unknown = activeElement;
    for (const segment of path) {
      if (!node || typeof node !== 'object') return undefined;
      const idx = Number(segment);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const maybeChildren = (node as any).elements;
      if (!Array.isArray(maybeChildren)) return undefined;
      const nextNode = maybeChildren[idx];
      if (!nextNode) return undefined;
      node = nextNode;
    }
    if (hasSource(node)) {
      const potential = node.source;
      if (potential && typeof potential === 'object')
        return potential as Record<string, unknown>;
    }
    return undefined;
  };

  const sources = control.elementPaths
    .map(getSourceForPath)
    .filter((s): s is Record<string, unknown> => !!s);

  // Collect property values across all targeted elements.
  const values = sources
    .map(s => s[control.property])
    .filter(v => v !== undefined);
  const uniqueValues = Array.from(
    new Set(
      values.map(v =>
        typeof v === 'string' || typeof v === 'number' ? v : JSON.stringify(v),
      ),
    ),
  );
  // If all targeted elements share same primitive value we display it; otherwise undefined (mixed state)
  let unifiedValue: unknown;
  if (control.type === 'speaker') {
    // Special handling for speaker control: unify only if all values are strings and equal
    const meta = getElementMetaObject(
      videoCreator.currentVideo,
      activeElement.source.id,
    );
    unifiedValue = meta?.speakerLabel;
  } else if (uniqueValues.length === 1) {
    unifiedValue = values[0];
  }

  // Specialized extraction for font weight if any (first non-undefined wins & all equal)
  const fontWeights = sources
    .map(s => s['font_weight'])
    .filter(w => typeof w === 'string');
  const uniqueFontWeights = Array.from(new Set(fontWeights));
  const unifiedFontWeight =
    uniqueFontWeights.length === 1 ? fontWeights[0] : undefined;

  switch (control.type) {
    case 'speaker':
      return (
        <SpeakerControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          onSpeakerControlChange={handleSpeakerControlChange}
          unifiedValue={unifiedValue}
        />
      );
    case 'text':
      return (
        <TextControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
        />
      );
    case 'color':
      return (
        <ColorControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
        />
      );
    case 'number':
      return (
        <NumberControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
        />
      );
    case 'font':
      return (
        <FontControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
          unifiedFontWeight={unifiedFontWeight}
        />
      );
    case 'image':
      return (
        <ImageControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
        />
      );
    case 'logo':
      return (
        <ImageControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
          isLogoControl={true}
        />
      );
    case 'video':
      return (
        <VideoControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          unifiedValue={unifiedValue}
        />
      );
    default:
      return null;
  }
};

export type { ControlSetting } from './ControlTypes';
